/**
 * Configuration Tab
 *
 * Orchestrates DNS Records, DKIM Selectors, and BIMI sections
 * as collapsible accordions in the domain detail page.
 */

import { useState, ReactNode } from "react";
import {
  FileText,
  Key,
  BadgeCheck,
  ChevronDown,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { Domain, DkimSelector } from "@/lib/api";
import { DnsSection } from "./DnsSection";
import { DkimSection } from "./DkimSection";
import { BimiSection } from "./BimiSection";

interface ConfigurationTabProps {
  domain: Domain;
  dkimSelectors: DkimSelector[];
  domainId: string;
  tier: string;
  onRefresh: () => void;
}

function CollapsibleSection({
  title,
  icon,
  badges,
  defaultOpen,
  children,
}: {
  title: string;
  icon: ReactNode;
  badges?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 sm:p-5 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
            {icon}
          </div>
          <span className="font-semibold text-slate-800">{title}</span>
          {!open && badges && (
            <div className="flex items-center gap-2 ml-2">{badges}</div>
          )}
        </div>
        <ChevronDown
          className={`w-5 h-5 text-slate-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div className="bg-slate-50 border-t border-slate-200 px-4 py-5 sm:px-5">
          {children}
        </div>
      )}
    </div>
  );
}

function StatusPill({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        ok
          ? "bg-emerald-50 text-emerald-700"
          : "bg-slate-100 text-slate-500"
      }`}
    >
      {ok ? (
        <CheckCircle2 className="w-3 h-3" />
      ) : (
        <XCircle className="w-3 h-3" />
      )}
      {label}
    </span>
  );
}

export function ConfigurationTab({
  domain,
  dkimSelectors,
  domainId,
  tier,
  onRefresh,
}: ConfigurationTabProps) {
  const currentPolicy = domain.current_policy || domain.dmarc_policy;
  const canSeeBimi =
    tier !== "free" && (tier !== "starter" || currentPolicy === "reject");

  const spfOk = domain.spf_configured ?? false;
  const dmarcOk = !!(currentPolicy && currentPolicy !== "none");
  const validDkim = dkimSelectors.filter((s) => s.is_valid).length;
  const totalDkim = dkimSelectors.length;

  return (
    <div className="space-y-4">
      <CollapsibleSection
        title="DNS Records"
        icon={<FileText className="w-5 h-5 text-amber-600" />}
        defaultOpen
        badges={
          <>
            <StatusPill ok={spfOk} label="SPF" />
            <StatusPill ok={dmarcOk} label="DMARC" />
          </>
        }
      >
        <DnsSection domain={domain} onRefresh={onRefresh} tier={tier} />
      </CollapsibleSection>

      <CollapsibleSection
        title="DKIM Selectors"
        icon={<Key className="w-5 h-5 text-amber-600" />}
        badges={
          <span className="text-xs text-slate-500">
            {validDkim}/{totalDkim} valid
          </span>
        }
      >
        <DkimSection
          selectors={dkimSelectors}
          domainId={domainId}
          onRefresh={onRefresh}
        />
      </CollapsibleSection>

      {canSeeBimi && (
        <CollapsibleSection
          title="BIMI"
          icon={<BadgeCheck className="w-5 h-5 text-amber-600" />}
          badges={
            currentPolicy === "reject" ? (
              <StatusPill ok label="Eligible" />
            ) : (
              <span className="text-xs text-slate-500">Requires p=reject</span>
            )
          }
        >
          <BimiSection domain={domain} domainId={domainId} tier={tier} />
        </CollapsibleSection>
      )}
    </div>
  );
}
